import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts'
// lucide icons — PieChart/LineChart aliased so they don't collide with the
// recharts imports of the same name above.
import {
  Calendar, Library, Table as TableIcon,
  PieChart as PieChartIcon, LineChart as LineChartIcon,
} from 'lucide-react'
import {
  statsProductPerformance, statsInstitutionalExhaust,
  type StatsWindow, type StatsTrendPoint, type ProductPerformance,
  type InstitutionalExhaust,
} from '../api'
import { ChatBubble } from '../components/ChatPanel'
import { statDefText } from '../statDefinitions'
import { useProductName } from '../productName'
import type { ComponentType as IconType } from 'react'

// ─────────────────────────────────────────────────────────────────────────
// Palette. Validated categorical set from the data-viz standard (OKLab CVD
// gates pass on the adjacent pairlist in both modes). We only draw a few
// slots; the rest are here so future series stay in the fixed order. Text /
// grid / axis follow the chart-chrome ink. Detected live so a theme toggle
// or OS change repaints without a reload.
// ─────────────────────────────────────────────────────────────────────────
const PALETTE = {
  light: {
    series: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
    ink: '#0b0b0b', muted: '#898781', grid: '#e1e0d9', axis: '#c3c2b7',
  },
  dark: {
    series: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
    ink: '#ffffff', muted: '#898781', grid: '#2c2c2a', axis: '#383835',
  },
}

function detectDark(): boolean {
  const attr = document.documentElement.getAttribute('data-theme')
  if (attr === 'dark') return true
  if (attr === 'light') return false
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}
function useThemePalette() {
  const [dark, setDark] = useState(detectDark)
  useEffect(() => {
    const recompute = () => setDark(detectDark())
    const mo = new MutationObserver(recompute)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    mq?.addEventListener?.('change', recompute)
    return () => { mo.disconnect(); mq?.removeEventListener?.('change', recompute) }
  }, [])
  return { dark, ...(dark ? PALETTE.dark : PALETTE.light) }
}

// ─── formatting helpers ───────────────────────────────────────────────────
const pct = (v: number | null | undefined, digits = 1) =>
  v == null ? '—' : `${(v * 100).toFixed(digits)}%`
const fmtDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
const fmtDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

// 180d / 1yr dropped — the corpus doesn't reach back that far yet
// (Ken 2026-08-07). Re-add when there's data to fill them.
// Continuous window dial (Ken 2026-08-23) — replaces the 30/90 buttons. A
// log-free drag track: left end = the corpus edge ("all"), right end = the last
// `minDays`. Landmarks at 7/30/90d. Commits throttled during drag. maxDays comes
// from the API (corpus_edge_days) so it can't drag past the data.
function StatsWindowDial({ value, onChange, maxDays, minDays = 7 }: {
  value: number; onChange: (days: number) => void; maxDays: number; minDays?: number
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const lastCommit = useRef(0)
  const [dragDays, setDragDays] = useState<number | undefined>(undefined)
  const hi = Math.max(minDays + 1, Math.round(maxDays))
  const shown = Math.min(hi, Math.max(minDays, dragDays === undefined ? value : dragDays))
  const f = (hi - shown) / (hi - minDays)   // 0 = left (all), 1 = right (recent)
  const atMax = shown >= hi
  const label = atMax ? 'all' : (shown % 7 === 0 ? `${shown / 7}w` : `${shown}d`)

  const daysAtX = (clientX: number) => {
    const el = trackRef.current
    if (!el) return shown
    const r = el.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return Math.round(hi - frac * (hi - minDays))
  }
  const commit = (d: number, force = false) => {
    const now = Date.now()
    if (!force && now - lastCommit.current < 180) return
    lastCommit.current = now
    onChange(d)
  }
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const d = daysAtX(e.clientX); setDragDays(d); commit(d, true)
  }
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    const d = daysAtX(e.clientX); setDragDays(d); commit(d)
  }
  const onUp = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    if (dragDays !== undefined) commit(dragDays, true)
    setDragDays(undefined)
  }
  return (
    <div className="flex select-none items-center gap-2"
      title={atMax ? `Window: all ${hi} days` : `Window: last ${shown} days`}>
      <span className="me-brand w-8 text-right text-[11px] font-normal normal-case tabular-nums me-text">{label}</span>
      <div
        ref={trackRef} role="slider" aria-label="Stats window"
        aria-valuemin={minDays} aria-valuemax={hi} aria-valuenow={shown}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        className="relative flex h-3.5 w-40 cursor-ew-resize touch-none items-center"
      >
        <div className="absolute left-0 right-0 h-[3px] rounded-full bg-[color:var(--me-border)]" />
        {/* window span: from the border (thumb) rightward to now */}
        <div className="absolute h-[3px] rounded-full" style={{ left: `${f * 100}%`, right: 0, background: 'var(--me-nav-color)' }} />
        {[7, 30, 90].filter((t) => t > minDays && t < hi).map((t) => (
          <div key={t} className="absolute h-1.5 w-px" style={{ left: `${((hi - t) / (hi - minDays)) * 100}%`, background: '#8E8E8E', opacity: 0.55 }} />
        ))}
        <div className="absolute rounded-full" style={{ left: `${f * 100}%`, width: 9, height: 9, transform: 'translateX(-50%)', background: 'var(--me-nav-color)', boxShadow: '0 0 0 1px var(--me-surface)' }} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Small presentational primitives
// ─────────────────────────────────────────────────────────────────────────
// Daily-average heading (Ken 2026-08-23): "Professional Communications per Day",
// with "per Day" italicized. Flips the pre-2026-08-23 persisted default to the new
// text, and italicizes a trailing "per Day" of whatever label is in effect.
const OLD_DAILY_AVG = 'Daily Averages: Communications Managed & New Matters Instantiated'
function renderDailyAvgHeading(raw: string) {
  const lbl = (!raw || raw === OLD_DAILY_AVG) ? 'Professional Communications per Day' : raw
  const m = lbl.match(/^(.*?)(\bper Day)\s*$/i)
  return m ? <>{m[1]}<em>{m[2]}</em></> : <>{lbl}</>
}

function Segmented<T extends string | number>({ options, value, onChange }: {
  options: { value: T; label: string; icon?: ComponentType<{ size?: number }> }[]
  value: T; onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex rounded-md border me-border p-0.5">
      {options.map((o) => (
        <button
          key={String(o.value)}
          onClick={() => onChange(o.value)}
          data-selected={o.value === value ? 'true' : undefined}
          className="me-tab !rounded-[5px] inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px]"
        >
          {o.icon && <o.icon size={13} />}
          {o.label}
        </button>
      ))}
    </div>
  )
}

function StatTile({ label, value, sub, tooltip }: { label: string; value: ReactNode; sub?: string; tooltip?: string }) {
  return (
    <div className="me-card px-4 py-3" title={tooltip}>
      <div className="me-chrome text-[10px] font-semibold uppercase tracking-wider me-text-mute">{label}</div>
      <div className="me-data mt-1 text-[26px] leading-none me-fg" style={{ fontVariantNumeric: 'normal' }}>{value}</div>
      {sub && <div className="mt-1.5 text-[11px] me-text-mute">{sub}</div>}
    </div>
  )
}

function ChartCard({ title, hint, actions, table, children, chartIcon: ChartIcon = TableIcon }: {
  title: string; hint?: string; actions?: ReactNode
  table?: ReactNode; children: ReactNode
  // Icon shown on the toggle when it will switch TO the chart (the label reads
  // "Chart"). Pass the icon that matches the chart type — a pie for the share
  // pies, a line for the trend charts. Defaults to the table icon.
  chartIcon?: IconType<{ size?: number }>
}) {
  // Table is the default view (Emerson 2026-08-06 — a table reads more
  // precisely than a chart, especially the pies). One click swaps to the chart.
  const [showTable, setShowTable] = useState(true)
  return (
    <div className="me-card flex flex-col p-4">
      <div className="mb-1 flex items-start justify-between gap-3">
        <div>
          <h3 className="me-brand text-[13px] font-semibold me-text">{title}</h3>
          {hint && <p className="mt-0.5 max-w-prose text-[11px] me-text-mute">{hint}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {table && (
            <button
              onClick={() => setShowTable((s) => !s)}
              data-selected={showTable ? 'true' : undefined}
              className="me-tab me-chrome inline-flex items-center gap-1 px-2 py-1 text-[11px]"
              title="Toggle table view (accessible twin)"
            >
              {/* Icon matches the destination: chart-type icon when the button
                  goes to the chart, table icon when it goes to the table. */}
              {showTable ? <ChartIcon size={12} /> : <TableIcon size={12} />} {showTable ? 'Chart' : 'Table'}
            </button>
          )}
        </div>
      </div>
      {showTable && table ? <div className="mt-2">{table}</div> : children}
    </div>
  )
}

function DataTable({ head, rows, numFont = 'data' }: {
  head: string[]; rows: (string | number)[][]
  // Font group for the numeric (non-first) columns. 'data' (default) is the
  // identifier/mono face; 'summary' matches the first-column label font
  // (agreements / litigation …) — easier to read in the share tables
  // (Ken 2026-08-11).
  numFont?: 'data' | 'summary'
}) {
  const numCls = numFont === 'summary' ? 'me-summary' : 'me-data'
  return (
    <div className="me-scroll max-h-[280px] overflow-auto rounded-md border me-border">
      <table className="me-chrome w-full text-[12px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
        <thead className="sticky top-0 bg-[color:var(--me-surface)]">
          <tr className="border-b me-border text-left me-text-mute">
            {head.map((h, i) => (
              <th key={h} className={`px-3 py-1.5 font-medium ${i === 0 ? '' : 'text-right'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-b me-border last:border-0">
              {r.map((c, ci) => (
                <td key={ci} className={`px-3 py-1.5 me-text ${ci === 0 ? 'me-summary' : `text-right ${numCls}`}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── custom tooltips ──────────────────────────────────────────────────────
function TrendTooltip({ active, payload, unitLabel }: any) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload as StatsTrendPoint
  return (
    <div className="me-card px-3 py-2 text-[12px] shadow-lg">
      <div className="mb-1 font-semibold me-text">{fmtDate(p.bucket)}</div>
      <div className="me-text">
        {p.value == null ? 'not measured' : pct(p.value)}
        <span className="me-text-mute"> · {p.numerator} / {p.denominator} {unitLabel}</span>
      </div>
      <div className="mt-0.5 text-[11px] me-text-mute">library size: {p.library_size}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Trend chart (1.1 / 1.2 / 1.3) — one series, x-axis is calendar OR library
// size (toggle lives in the section header). connectNulls=false so the
// pre-measurement gap reads as a gap, not a zero.
// ─────────────────────────────────────────────────────────────────────────
function TrendChart({ points, xMode, color, muted, grid, axis, unitLabel, emptyMsg }: {
  points: StatsTrendPoint[]; xMode: 'date' | 'library'
  color: string; muted: string; grid: string; axis: string
  unitLabel: string; emptyMsg?: string
}) {
  const hasData = points.some((p) => p.value != null)
  if (!hasData) {
    return (
      <div className="grid h-[220px] place-items-center text-center text-[12px] me-text-mute">
        <div className="max-w-xs">{emptyMsg ?? 'No data in this window yet.'}</div>
      </div>
    )
  }
  const tick = { fill: muted, fontSize: 11 }
  return (
    <div className="h-[220px]" aria-label="trend chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
          <CartesianGrid stroke={grid} strokeWidth={1} vertical={false} />
          {xMode === 'date' ? (
            <XAxis dataKey="bucket" tickFormatter={fmtDate} tick={tick} stroke={axis}
              tickLine={false} minTickGap={24} />
          ) : (
            <XAxis dataKey="library_size" type="number" tick={tick} stroke={axis}
              tickLine={false} domain={['dataMin', 'dataMax']}
              label={{ value: 'matter-library size', position: 'insideBottom', offset: -2, fill: muted, fontSize: 10 }} />
          )}
          <YAxis tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={tick} stroke={axis}
            tickLine={false} width={44} />
          <Tooltip content={<TrendTooltip unitLabel={unitLabel} />} cursor={{ stroke: muted, strokeWidth: 1 }} />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2}
            dot={{ r: 3, fill: color, strokeWidth: 0 }} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Share pie (2.1 / 2.2) — part-to-whole across members: the top 4 by count
// plus a 5th "Other" slice that folds the tail. Five slices sit inside the
// data-viz ≤6 part-to-whole limit. Slices are colored in slot order so ring
// neighbors are palette-adjacent (the validated adjacent pairlist), and
// "Other" is neutral gray. Slice % is labeled outside in muted ink; the
// legend + tooltip + table carry names and raw counts.
// ─────────────────────────────────────────────────────────────────────────
type PieDatum = { label: string; count: number; matterId?: number | null }

// Display renames for issue-kind labels (Ken 2026-08-07). The underlying L3
// enum values are unchanged — this is presentation only. ('Other' being a
// heavy bucket is a separate L3 problem, shelved.)
const KIND_LABELS: Record<string, string> = {
  'vendor deal': 'agreements',
  'personnel matter': 'personnel',
  'Other': 'other',
}
const kindLabel = (k: string) => KIND_LABELS[k] ?? k

// Label for the folded-tail slice. Lowercase per Ken 2026-08-11 (matches the
// other kind labels — agreements / litigation …). Also the sentinel the pie
// uses to paint that slice neutral-gray, so keep the two in sync.
const OTHER_LABEL = 'other'
// Real members already come sorted desc from the backend. Keep the top 4;
// fold the rest (plus any pre-aggregated remainder) into "other".
function toPie(rows: PieDatum[], carriedOther = 0): PieDatum[] {
  const top = rows.slice(0, 4)
  const tail = rows.slice(4).reduce((s, r) => s + r.count, 0) + carriedOther
  const out: PieDatum[] = top.map((r) => ({ ...r }))
  if (tail > 0) out.push({ label: OTHER_LABEL, count: tail, matterId: null })
  return out
}

function PieTooltip({ active, payload, total, unit }: any) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload as PieDatum
  const share = total ? (p.count / total) * 100 : 0
  return (
    <div className="me-card max-w-[260px] px-3 py-2 text-[12px] shadow-lg">
      <div className="mb-1 font-semibold me-text">{p.label}</div>
      <div className="me-text">{p.count} {unit} <span className="me-text-mute">· {share.toFixed(1)}%</span></div>
    </div>
  )
}

function pieLabelRenderer(muted: string) {
  const RAD = Math.PI / 180
  return (props: any) => {
    const { cx, cy, midAngle, outerRadius, percent } = props
    if (percent < 0.04) return null
    const r = outerRadius + 16
    const x = cx + r * Math.cos(-midAngle * RAD)
    const y = cy + r * Math.sin(-midAngle * RAD)
    return (
      <text x={x} y={y} fill={muted} fontSize={11} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {Math.round(percent * 100)}%
      </text>
    )
  }
}

function SharePie({ data, colors, muted, unit, onSliceClick }: {
  data: PieDatum[]; colors: string[]; muted: string; unit: string
  onSliceClick?: (d: PieDatum) => void
}) {
  const total = data.reduce((s, d) => s + d.count, 0) || 1
  return (
    <div className="h-[300px]" aria-label="share pie chart">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <Pie data={data} dataKey="count" nameKey="label" cx="50%" cy="46%"
            innerRadius={0} outerRadius={78} paddingAngle={1} isAnimationActive={false}
            stroke="var(--me-surface)" strokeWidth={2}
            label={pieLabelRenderer(muted)} labelLine={false}
            onClick={onSliceClick ? ((d: any) => onSliceClick(d?.payload ?? d)) : undefined}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.label === OTHER_LABEL ? muted : colors[i % colors.length]}
                cursor={onSliceClick && d.matterId != null ? 'pointer' : undefined} />
            ))}
          </Pie>
          <Tooltip content={<PieTooltip total={total} unit={unit} />} />
          <Legend wrapperStyle={{ fontSize: 11, color: muted }} iconType="circle" />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────
export default function Stats() {
  const navigate = useNavigate()
  const pal = useThemePalette()
  const product = useProductName()
  // Default 30d — with only ~2 months of corpus, 90d == the whole corpus, which
  // reads as "total" rather than a window (Ken 2026-08-10). 30d shows a real subset.
  const [window, setWindow] = useState<StatsWindow>(30)

  const perf = useQuery<ProductPerformance>({
    queryKey: ['stats', 'product-performance', window],
    queryFn: () => statsProductPerformance(window),
    placeholderData: keepPreviousData,
  })
  const exhaust = useQuery<InstitutionalExhaust>({
    queryKey: ['stats', 'institutional-exhaust', window],
    queryFn: () => statsInstitutionalExhaust(window),
    placeholderData: keepPreviousData,
  })

  const s = perf.data?.summary
  // Per-day averages, rounded to whole numbers — no decimals (Ken 2026-08-11).
  const n1 = (v?: number | null) => (v == null ? '—' : Math.round(v).toLocaleString())
  const kindPie = useMemo(
    () => toPie((exhaust.data?.issue_kind_pareto ?? []).map((r) => ({ label: kindLabel(r.kind), count: r.count }))),
    [exhaust.data],
  )
  const matterPie = useMemo(() => {
    const rows = exhaust.data?.matter_pareto ?? []
    const real = rows.filter((r) => r.matter_id != null)
      .map((r) => ({ label: r.name, count: r.count, matterId: r.matter_id }))
    const carried = rows.filter((r) => r.matter_id == null).reduce((a, r) => a + r.count, 0)
    return toPie(real, carried)
  }, [exhaust.data])
  const kindTotal = kindPie.reduce((a, d) => a + d.count, 0) || 1
  const matterTotal = matterPie.reduce((a, d) => a + d.count, 0) || 1

  const loading = perf.isLoading || exhaust.isLoading
  const refetching = (perf.isFetching || exhaust.isFetching) && !loading

  return (
    <div className="me-scroll h-full overflow-auto bg-[color:var(--me-bg)]">
      <div className="mx-auto max-w-[1180px] px-6 py-6">
        {/* Global filter (scopes everything below). Title removed per Emerson
            2026-08-11; filter sits alone, right-aligned, and the tighter margin
            pulls the content below up into the reclaimed space. */}
        <div className="mb-3 flex flex-wrap items-center justify-end gap-3">
          <StatsWindowDial value={window} onChange={setWindow} maxDays={perf.data?.corpus_edge_days ?? 90} />
        </div>

        <div className={refetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          {loading ? (
            <div className="grid h-64 place-items-center">
              <div className="size-7 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Daily Average — per-day funnel metrics. Heading text is
                     QA-editable (productName.statsDailyAvgLabel). ──────────── */}
              <h2 className="me-brand mb-1 text-[15px] font-normal me-text">{renderDailyAvgHeading(product.statsDailyAvgLabel)}</h2>
              {/* Denominator note: per-day tiles divide by working days (Mon–Fri),
                  not calendar days — weekends are ~5% of volume (Ken 2026-08-23). */}
              <div className="me-text-mute mb-3 text-[11px]">
                per working day (Mon–Fri){perf.data?.business_days ? ` · ${perf.data.business_days} business days in window` : ''}
              </div>
              <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatTile label="Filed Emails" value={n1(s?.emails_filed_per_day)}
                  tooltip={statDefText('deduped_email')} />
                <StatTile label="Email Issues" value={n1(s?.issues_filed_per_day)}
                  tooltip={statDefText('email_issues')} />
                <StatTile label="Documents" value={n1(s?.documents_per_day)}
                  sub="sent or received" tooltip={statDefText('documents')} />
                <StatTile label="(Estimated) New Matters" value={n1(s?.new_matters_per_day)}
                  tooltip={statDefText('new_matters')} />
              </div>

              {/* ── Matters (was Communications Overhead) ───────────────── */}
              <h2 className="me-brand mb-3 text-[15px] font-normal me-text">Matters Active At Least Once Since June 30, 2027</h2>

              {/* Active-matter total — narrow, content-hugging box. Number, then
                  "TOTAL" to its right, both centered on the number's middle
                  (Ken 2026-08-10). Count already excludes archived (closed). */}
              <div className="mb-3">
                <div className="me-card inline-flex items-center gap-2 px-4 h-[64px]" title={statDefText('total_matters')}>
                  <div className="me-data text-[26px] leading-none me-fg" style={{ fontVariantNumeric: 'normal' }}>
                    {String(s?.total_matters ?? '—')}
                  </div>
                  <span className="me-chrome text-[10px] font-semibold uppercase tracking-wider me-text-mute">Total</span>
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-2">
                <ChartCard
                  title="Email Issues by Matter Type"
                  chartIcon={PieChartIcon}
                  table={<DataTable head={['Type', 'Emails', 'Share']} numFont="summary"
                    rows={kindPie.map((d) => [d.label, d.count, `${((d.count / kindTotal) * 100).toFixed(1)}%`])} />}
                >
                  <SharePie data={kindPie} colors={pal.series} muted={pal.muted} unit="emails" />
                </ChartCard>

                <ChartCard
                  title="Email Issues by Matter"
                  chartIcon={PieChartIcon}
                  table={<DataTable head={['Matter', 'Emails', 'Share']} numFont="summary"
                    rows={matterPie.map((d) => [d.label, d.count, `${((d.count / matterTotal) * 100).toFixed(1)}%`])} />}
                >
                  <SharePie data={matterPie} colors={pal.series} muted={pal.muted} unit="emails"
                    onSliceClick={(d) => { if (d.matterId != null) navigate(`/workspace?matter=${d.matterId}`) }} />
                </ChartCard>
              </div>

              <p className="mt-6 text-[11px] me-text-mute">
                Tenant-scoped to your organization · generated {perf.data ? new Date(perf.data.generated_at).toLocaleString() : '—'}
              </p>
            </>
          )}
        </div>
      </div>
      {/* Floating AI bubble — same bottom-right FAB as Main (Ken 2026-08-07). */}
      <ChatBubble scopeKind="global" scopeId={null} scopeLabel="Statistics" />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Product-performance section — refile + fragmentation trend charts. Lifted
// off the Stats page onto the bottom of QA (Ken 2026-08-07: "it doesn't
// belong here"). Self-contained: own window filter, x-axis toggle, and query.
// ─────────────────────────────────────────────────────────────────────────
export function ProductPerformanceSection() {
  const pal = useThemePalette()
  const [window, setWindow] = useState<StatsWindow>(90)
  const [xMode, setXMode] = useState<'date' | 'library'>('date')
  const perf = useQuery<ProductPerformance>({
    queryKey: ['stats', 'product-performance', window],
    queryFn: () => statsProductPerformance(window),
    placeholderData: keepPreviousData,
  })
  const measuredSince = perf.data?.measurement_since ?? null
  const trendProps = { xMode, muted: pal.muted, grid: pal.grid, axis: pal.axis }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
        <div className="flex items-center gap-2">
          <StatsWindowDial value={window} onChange={setWindow} maxDays={perf.data?.corpus_edge_days ?? 90} />
          <span className="text-[11px] me-text-mute">x-axis</span>
          <Segmented
            options={[
              { value: 'date', label: 'Date', icon: Calendar },
              { value: 'library', label: 'Library size', icon: Library },
            ]}
            value={xMode}
            onChange={setXMode}
          />
        </div>
      </div>
      <p className="mb-3 text-[11px] me-text-mute">
        Toggle the x-axis to test the fragmentation-grows-with-scale hypothesis: the same rates plotted against the matter library's size rather than the calendar.
      </p>

      <div className="grid gap-3 lg:grid-cols-2">
        <ChartCard
          title="Refile rate"
          chartIcon={LineChartIcon}
          hint="The AI's own correction signal — refiles ÷ filings. Measurement began when re-file logging shipped; no history before that."
          table={<DataTable head={['Bucket', 'Rate', 'Refiles', 'Filings', 'Lib size']}
            rows={(perf.data?.refile_rate ?? []).map((p) => [fmtDate(p.bucket), pct(p.value), p.numerator, p.denominator, p.library_size])} />}
        >
          <TrendChart points={perf.data?.refile_rate ?? []} color={pal.series[0]} unitLabel="refiles/filings"
            emptyMsg={`Re-file measurement began ${fmtDateTime(measuredSince)}. No re-files recorded in this window yet — the line fills in as corrections happen.`}
            {...trendProps} />
        </ChartCard>

        <ChartCard
          title="Fragmentation rate"
          chartIcon={LineChartIcon}
          hint="Narrower than a refile: the founding email of a matter later moved out — the new-matter decision itself was wrong. Fragmentations ÷ matters created."
          table={<DataTable head={['Bucket', 'Rate', 'Fragmented', 'Created', 'Lib size']}
            rows={(perf.data?.fragmentation_rate ?? []).map((p) => [fmtDate(p.bucket), pct(p.value), p.numerator, p.denominator, p.library_size])} />}
        >
          <TrendChart points={perf.data?.fragmentation_rate ?? []} color={pal.series[1]} unitLabel="fragmented/created"
            emptyMsg={`Measurement began ${fmtDateTime(measuredSince)}. No fragmenting re-files recorded in this window yet.`}
            {...trendProps} />
        </ChartCard>
      </div>
    </div>
  )
}
